export function canBuildCrossword(words) {
    if (words.length < 2)
        return false;
    function findOverlap(word1, word2) {
        for (let i = 0; i < word1.length; i++) {
            for (let j = 0; j < word2.length; j++) {
                if (word1[i] === word2[j]) {
                    return true;
                }
            }
        }
        return false;
    }
    // Create an adjacency list representation of word connections
    // Each word is a vertex, and edges exist between words that share characters
    const graph = {};
    words.forEach((word) => {
        graph[word] = [];
    });
    // Build the graph
    for (let i = 0; i < words.length; i++) {
        for (let j = i + 1; j < words.length; j++) {
            if (findOverlap(words[i], words[j])) {
                graph[words[i]].push(words[j]);
                graph[words[j]].push(words[i]);
            }
        }
    }
    /**
     * Performs Depth-First Search to check if all words are connected
     */
    function dfs(word, visited) {
        visited.add(word);
        for (const neighbor of graph[word]) {
            if (!visited.has(neighbor)) {
                dfs(neighbor, visited);
            }
        }
    }
    const visited = new Set();
    dfs(words[0], visited);
    // If all words are visited, a crossword can be built
    return visited.size === words.length;
}
export class Crossword {
    constructor(words) {
        this.grid = [];
        this.wordsAndClues = words.map((w) => ({
            answer: w.answer.toUpperCase(),
            clue: w.clue,
        }));
        this.placedWords = [];
        this.hints = { across: [], down: [] };
        this.sorted = [...this.wordsAndClues];
        this.sorted.sort((a, b) => b.answer.length - a.answer.length);
        let length = 0, breadth = 0, countHorizontal = 0, countVertical = 0;
        for (let i = 0; i < this.sorted.length; i++) {
            if (i === 0) {
                length += this.sorted[i].answer.length;
                countHorizontal++;
            }
            else {
                if (i % 2 === 0) {
                    length += this.sorted[i].answer.length;
                    countHorizontal++;
                }
                else {
                    breadth += this.sorted[i].answer.length;
                    countVertical++;
                }
            }
        }
        this.maxGridSize = [
            (length - (countHorizontal - 1)) * 2,
            (breadth - (countVertical - 1)) * 2,
        ];
    }
    getMaxGridSize() {
        return this.maxGridSize;
    }
    generate() {
        let placed = [];
        while (placed.length != this.wordsAndClues.length) {
            this.grid = Array(this.maxGridSize[1])
                .fill('')
                .map(() => Array(this.maxGridSize[0]).fill('#'));
            placed = [];
            const visited = [], intersections = [];
            // Select a random interger between 0 and length of words to get index of current word.
            const index = Math.floor(Math.random() * this.wordsAndClues.length);
            // currentWord.index = 2;
            let currentWord = {
                index,
                answer: this.wordsAndClues[index].answer,
                clue: this.wordsAndClues[index].clue,
            };
            let previousWord = {
                index,
                answer: this.wordsAndClues[index].answer,
                clue: this.wordsAndClues[index].clue,
            };
            const longestWord = this.wordsAndClues.reduce((a, b) => {
                if (!a)
                    return b;
                if (!b)
                    return a;
                return a.answer.length >= b.answer.length ? a : b;
            });
            const secondLongestWord = this.wordsAndClues
                .filter((wordAndClue) => wordAndClue.answer !== longestWord.answer)
                .reduce((a, b) => {
                if (!a)
                    return b;
                if (!b)
                    return a;
                return a.answer.length >= b.answer.length ? a : b;
            });
            let currentPos = [
                longestWord.answer.length - 1,
                secondLongestWord.answer.length - 1,
            ], previousPos;
            let wordPlaced = false;
            while (visited.length !== this.wordsAndClues.length) {
                wordPlaced = false;
                if (placed.length === 0) {
                    for (let i = 0; i < currentWord.answer.length; i++) {
                        this.grid[currentPos[0]][currentPos[1] + i] = currentWord.answer[i];
                    }
                    currentWord.direction = 'horizontal';
                    currentWord.startPos = [...currentPos];
                    previousPos = currentPos;
                    currentPos = [
                        currentPos[0],
                        currentPos[1] + currentWord.answer.length - 1,
                    ];
                    wordPlaced = true;
                }
                else {
                    const commonLetters = this.returnCommonLetters(previousWord.answer, currentWord.answer);
                    if (commonLetters[0]) {
                        if (previousWord.direction === 'horizontal') {
                            commonLetters.forEach((commonLetter) => {
                                previousWord.answer.split('').forEach((letter, index) => {
                                    if (letter === commonLetter) {
                                        if (!wordPlaced) {
                                            const commonLetterPos = [
                                                currentPos[0],
                                                currentPos[1] - previousWord.answer.length + 1 + index,
                                            ];
                                            if (!intersections.find((inter) => inter.commonLetter === commonLetter &&
                                                inter.commonPos[0] === commonLetterPos[0] &&
                                                inter.commonPos[1] === commonLetterPos[1])) {
                                                previousPos = currentPos;
                                                currentPos = [
                                                    currentPos[0] -
                                                        currentWord.answer.indexOf(commonLetter),
                                                    currentPos[1] -
                                                        previousWord.answer.length +
                                                        1 +
                                                        index,
                                                ];
                                                if (this.canPlaceVertical(currentWord.answer, currentPos[0], currentPos[1], commonLetter, commonLetterPos)) {
                                                    for (let i = 0; i < currentWord.answer.length; i++) {
                                                        this.grid[currentPos[0] + i][currentPos[1]] =
                                                            currentWord.answer[i];
                                                    }
                                                    currentWord.direction = 'vertical';
                                                    currentWord.startPos = [...currentPos];
                                                    previousPos = currentPos;
                                                    currentPos = [
                                                        currentPos[0] + currentWord.answer.length - 1,
                                                        currentPos[1],
                                                    ];
                                                    wordPlaced = true;
                                                    intersections.push({
                                                        commonLetter,
                                                        commonPos: [...commonLetterPos],
                                                    });
                                                }
                                                else {
                                                    currentPos = previousPos;
                                                }
                                            }
                                            else {
                                                wordPlaced = false;
                                            }
                                        }
                                    }
                                });
                            });
                        }
                        else {
                            commonLetters.forEach((commonLetter) => {
                                previousWord.answer.split('').forEach((letter, index) => {
                                    if (letter === commonLetter) {
                                        if (!wordPlaced) {
                                            const commonLetterPos = [
                                                currentPos[0] - previousWord.answer.length + 1 + index,
                                                currentPos[1],
                                            ];
                                            if (!intersections.find((inter) => inter.commonLetter === commonLetter &&
                                                inter.commonPos[0] === commonLetterPos[0] &&
                                                inter.commonPos[1] === commonLetterPos[1])) {
                                                previousPos = currentPos;
                                                currentPos = [
                                                    currentPos[0] -
                                                        previousWord.answer.length +
                                                        1 +
                                                        index,
                                                    currentPos[1] -
                                                        currentWord.answer.indexOf(commonLetter),
                                                ];
                                                if (this.canPlaceHorizontal(currentWord.answer, currentPos[0], currentPos[1], commonLetter, commonLetterPos)) {
                                                    for (let i = 0; i < currentWord.answer.length; i++) {
                                                        this.grid[currentPos[0]][currentPos[1] + i] =
                                                            currentWord.answer[i];
                                                    }
                                                    currentWord.direction = 'horizontal';
                                                    currentWord.startPos = [...currentPos];
                                                    previousPos = currentPos;
                                                    currentPos = [
                                                        currentPos[0],
                                                        currentPos[1] + currentWord.answer.length - 1,
                                                    ];
                                                    wordPlaced = true;
                                                    intersections.push({
                                                        commonLetter,
                                                        commonPos: [...commonLetterPos],
                                                    });
                                                }
                                                else {
                                                    currentPos = previousPos;
                                                }
                                            }
                                            else {
                                                wordPlaced = false;
                                            }
                                        }
                                    }
                                });
                            });
                        }
                    }
                    else {
                        wordPlaced = false;
                    }
                }
                if (wordPlaced) {
                    placed.push(currentWord);
                    visited.push(currentWord);
                    previousWord = currentWord;
                }
                else {
                    visited.push(currentWord);
                    currentWord.direction = undefined;
                    currentWord.startPos = undefined;
                }
                // if (currentWord.index == this.wordsAndClues.length - 1) {
                //   currentWord.index = 0;
                // } else {
                //   currentWord.index = currentWord.index + 1;
                // }
                let isNewWord = false;
                while (!isNewWord) {
                    currentWord.index = Math.floor(Math.random() * this.wordsAndClues.length);
                    currentWord = {
                        ...currentWord,
                        answer: this.wordsAndClues[currentWord.index].answer,
                        clue: this.wordsAndClues[currentWord.index].clue,
                    };
                    if (!visited.find((v) => v.answer === currentWord.answer) ||
                        visited.length === this.wordsAndClues.length) {
                        isNewWord = true;
                    }
                }
            }
            if (visited.length != placed.length) {
                const unPlaced = visited.filter((v) => !placed.some((p) => p['answer'] === v['answer']));
                for (let i = 0; i < unPlaced.length; i++) {
                    for (let j = 0; j < placed.length; j++) {
                        if (!unPlaced[i].startPos) {
                            const commonLetters = this.returnCommonLetters(unPlaced[i].answer, placed[j].answer);
                            if (commonLetters[0]) {
                                if (placed[j].direction === 'horizontal') {
                                    currentPos = [
                                        placed[j].startPos[0],
                                        placed[j].startPos[1] + placed[j].answer.length - 1,
                                    ];
                                    commonLetters.forEach((commonLetter) => {
                                        placed[j].answer.split('').forEach((letter, index) => {
                                            if (letter === commonLetter) {
                                                if (!unPlaced[i].startPos) {
                                                    const commonLetterPos = [
                                                        placed[j].startPos[0],
                                                        placed[j].startPos[1] + index,
                                                    ];
                                                    if (!intersections.find((inter) => inter.commonLetter === commonLetter &&
                                                        inter.commonPos[0] === commonLetterPos[0] &&
                                                        inter.commonPos[1] === commonLetterPos[1])) {
                                                        previousPos = currentPos;
                                                        currentPos = [
                                                            currentPos[0] -
                                                                unPlaced[i].answer.indexOf(commonLetter),
                                                            currentPos[1] -
                                                                placed[j].answer.length +
                                                                1 +
                                                                index,
                                                        ];
                                                        if (this.canPlaceVertical(unPlaced[i].answer, currentPos[0], currentPos[1], commonLetter, commonLetterPos)) {
                                                            for (let x = 0; x < unPlaced[i].answer.length; x++) {
                                                                this.grid[currentPos[0] + x][currentPos[1]] =
                                                                    unPlaced[i].answer[x];
                                                            }
                                                            unPlaced[i].direction = 'vertical';
                                                            unPlaced[i].startPos = [...currentPos];
                                                            placed.push(unPlaced[i]);
                                                            previousPos = currentPos;
                                                            currentPos = [
                                                                currentPos[0] + unPlaced[i].answer.length - 1,
                                                                currentPos[1],
                                                            ];
                                                            intersections.push({
                                                                commonLetter,
                                                                commonPos: [...commonLetterPos],
                                                            });
                                                        }
                                                        else {
                                                            currentPos = previousPos;
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                    });
                                }
                                else {
                                    currentPos = [
                                        placed[j].startPos[0] + placed[j].answer.length - 1,
                                        placed[j].startPos[1],
                                    ];
                                    commonLetters.forEach((commonLetter) => {
                                        placed[j].answer.split('').forEach((letter, index) => {
                                            if (letter === commonLetter) {
                                                if (!unPlaced[i].startPos) {
                                                    const commonLetterPos = [
                                                        placed[j].startPos[0] + index,
                                                        placed[j].startPos[1],
                                                    ];
                                                    if (!intersections.find((inter) => inter.commonLetter === commonLetter &&
                                                        inter.commonPos[0] === commonLetterPos[0] &&
                                                        inter.commonPos[1] === commonLetterPos[1])) {
                                                        previousPos = currentPos;
                                                        currentPos = [
                                                            currentPos[0] -
                                                                placed[j].answer.length +
                                                                1 +
                                                                index,
                                                            currentPos[1] -
                                                                unPlaced[i].answer.indexOf(commonLetter),
                                                        ];
                                                        if (this.canPlaceHorizontal(unPlaced[i].answer, currentPos[0], currentPos[1], commonLetter, commonLetterPos)) {
                                                            for (let x = 0; x < unPlaced[i].answer.length; x++) {
                                                                this.grid[currentPos[0]][currentPos[1] + x] =
                                                                    unPlaced[i].answer[x];
                                                            }
                                                            unPlaced[i].direction = 'horizontal';
                                                            unPlaced[i].startPos = [...currentPos];
                                                            placed.push(unPlaced[i]);
                                                            previousPos = currentPos;
                                                            currentPos = [
                                                                currentPos[0],
                                                                currentPos[1] + unPlaced[i].answer.length - 1,
                                                            ];
                                                            intersections.push({
                                                                commonLetter,
                                                                commonPos: [...commonLetterPos],
                                                            });
                                                        }
                                                        else {
                                                            currentPos = previousPos;
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
        this.placedWords = placed;
        return this.grid;
    }
    returnCommonLetters(str1, str2) {
        const set1 = new Set(str1);
        const set2 = new Set(str2);
        return [...set1].filter((char) => set2.has(char));
    }
    canPlaceHorizontal(answer, startRow, startCol, commonLetter, commonLetterPos) {
        // Prevent out of bounds.
        if (startCol < 0 ||
            startRow < 0 ||
            startCol + answer.length > this.maxGridSize[0] - 1)
            return false;
        // Prevent already filled cells.
        if (this.grid[startRow][startCol] !== '#' &&
            this.grid[startRow][startCol] !== commonLetter)
            return false;
        // Prevent adjacents.
        if (
        // this.grid[startRow + i] &&
        this.grid[startRow][startCol - 1] &&
            this.grid[startRow][startCol - 1] !== '#')
            return false;
        if (
        // this.grid[startRow] &&
        this.grid[startRow][startCol + answer.length] &&
            this.grid[startRow][startCol + answer.length] !== '#')
            return false;
        let commonLetterPassed = false;
        for (let i = 0; i < answer.length; i++) {
            // Prevent already filled cells.
            if (this.grid[startRow][startCol + i] !== '#' &&
                this.grid[startRow][startCol + i] !== commonLetter &&
                commonLetterPassed)
                return false;
            if (this.grid[startRow][startCol + i] === commonLetter &&
                startCol + i !== commonLetterPos[1])
                return false;
            // Prevent adjacents.
            if (this.grid[startRow + 1] &&
                this.grid[startRow + 1][startCol + i] &&
                this.grid[startRow + 1][startCol + i] !== '#' &&
                this.grid[startRow][startCol + i] !== commonLetter)
                return false;
            if (this.grid[startRow - 1] &&
                this.grid[startRow - 1][startCol + i] &&
                this.grid[startRow - 1][startCol + i] !== '#' &&
                this.grid[startRow][startCol + i] !== commonLetter)
                return false;
            if (this.grid[startRow][startCol + i] === commonLetter &&
                startRow === commonLetterPos[0] &&
                startCol + i === commonLetterPos[1]) {
                commonLetterPassed = true;
            }
        }
        return true;
    }
    canPlaceVertical(answer, startRow, startCol, commonLetter, commonLetterPos) {
        // Prevent out of bounds.
        if (startCol < 0 ||
            startRow < 0 ||
            startRow + answer.length > this.maxGridSize[1] - 1)
            return false;
        // Prevent already filled cells.
        if (this.grid[startRow][startCol] !== '#' &&
            this.grid[startRow][startCol] !== commonLetter)
            return false;
        // Prevent adjacents.
        if (this.grid[startRow - 1] &&
            this.grid[startRow - 1][startCol] &&
            this.grid[startRow - 1][startCol] !== '#')
            return false;
        if (this.grid[startRow + answer.length] &&
            this.grid[startRow + answer.length][startCol] &&
            this.grid[startRow + answer.length][startCol] !== '#')
            return false;
        let commonLetterPassed = false;
        for (let i = 0; i < answer.length; i++) {
            // Prevent already filled cells.
            if (this.grid[startRow + i][startCol] !== '#' &&
                this.grid[startRow + i][startCol] !== commonLetter &&
                commonLetterPassed)
                return false;
            if (this.grid[startRow + i][startCol] === commonLetter &&
                startRow + i !== commonLetterPos[0])
                return false;
            // Prevent adjacents.
            if (this.grid[startRow + i] &&
                this.grid[startRow + i][startCol + 1] &&
                this.grid[startRow + i][startCol + 1] !== '#' &&
                this.grid[startRow + i][startCol] !== commonLetter)
                return false;
            if (this.grid[startRow + i] &&
                this.grid[startRow + i][startCol - 1] &&
                this.grid[startRow + i][startCol - 1] !== '#' &&
                this.grid[startRow + i][startCol] !== commonLetter)
                return false;
            if (this.grid[startRow + i][startCol] === commonLetter) {
                commonLetterPassed = true;
            }
        }
        return true;
    }
    generateHints() {
        let number = 1;
        for (let i = 0; i < this.placedWords.length; i++) {
            let exists;
            if (this.placedWords[i].direction === 'horizontal') {
                exists = this.hints.down.find((word) => word &&
                    word.start &&
                    word.start[0] === this.placedWords[i].startPos[0] &&
                    word.start[1] === this.placedWords[i].startPos[1]);
                const hint = {
                    number: exists && exists.number ? exists.number : number,
                    clue: this.placedWords[i].clue,
                    start: this.placedWords[i].startPos,
                    answer: this.placedWords[i].answer,
                    length: this.placedWords[i].answer.length,
                };
                this.hints.across.push(hint);
            }
            else {
                exists = this.hints.across.find((word) => word &&
                    word.start &&
                    word.start[0] === this.placedWords[i].startPos[0] &&
                    word.start[1] === this.placedWords[i].startPos[1]);
                const hint = {
                    number: exists && exists.number ? exists.number : number,
                    clue: this.placedWords[i].clue,
                    start: this.placedWords[i].startPos,
                    answer: this.placedWords[i].answer,
                    length: this.placedWords[i].answer.length,
                };
                this.hints.down.push(hint);
            }
            if (!exists)
                number++;
        }
    }
}
export function generateGrid(hints) {
    const verticalStart = hints.down.reduce((prev, curr) => prev.start[0] < curr.start[0] ? prev : curr);
    const verticalEnd = hints.down.reduce((prev, curr) => prev.start[0] + prev.length > curr.start[0] + curr.length ? prev : curr);
    const horizontalStart = hints.across.reduce((prev, curr) => prev.start[1] < curr.start[1] ? prev : curr);
    const horizontalEnd = hints.across.reduce((prev, curr) => prev.start[1] + prev.length > curr.start[1] + curr.length ? prev : curr);
    const length = horizontalEnd.start[1] + horizontalEnd.length - horizontalStart.start[1];
    const height = verticalEnd.start[0] + verticalEnd.length - verticalStart.start[0];
    const newGrid = Array(height)
        .fill(null)
        .map(() => Array(length).fill({ type: 'blank' }));
    hints.across.forEach((hint, _k) => {
        newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
            type: 'word',
            hintNumber: hint.number,
            hints: [{ ...hint, direction: 'across' }],
            letter: hint.answer.split('')[0],
        };
        for (let i = 1; i < hint.length; i++) {
            newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1] + i] = {
                type: 'word',
                hints: [{ ...hint, direction: 'across' }],
                letter: hint.answer.split('')[i],
            };
        }
    });
    hints.down.forEach((hint, _k) => {
        if (newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]].type !== 'word') {
            newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
                type: 'word',
                hintNumber: hint.number,
                hints: [{ ...hint, direction: 'down' }],
                letter: hint.answer.split('')[0],
            };
        }
        else {
            newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
                ...newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]],
                hintNumber: hint.number,
                hints: [
                    ...(newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]].hints || []),
                    { ...hint, direction: 'down' },
                ],
                intersection: true,
                letter: hint.answer.split('')[0],
            };
        }
        for (let i = 1; i < hint.length; i++) {
            if (newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]].type !== 'word') {
                newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]] = {
                    type: 'word',
                    hints: [{ ...hint, direction: 'down' }],
                    letter: hint.answer.split('')[i],
                };
            }
            else {
                newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]] = {
                    ...newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]],
                    hints: [
                        ...(newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]].hints || []),
                        { ...hint, direction: 'down' },
                    ],
                    intersection: true,
                    letter: hint.answer.split('')[i],
                };
            }
        }
    });
    return { hints, newGrid, length, height };
}
